'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button, Avatar, StatusPill, Clock, Icon } from '@/components/ds';
import {
  blockedBandsForStaffDay,
  hhmmToMin,
  isFullyBlocked,
  type BreakEntry,
  type TimeOffEntry,
  type WorkingHoursEntry,
} from './staff-availability';

/**
 * CalendarPage — T5b re-skin.
 *
 * Day view with staff columns. Appointments are positioned by their real
 * `startsAt`/`endsAt` from the tRPC query. All data, mutations, and role-gating
 * are identical to T21a — this is a presentation-only change.
 *
 * Status mapping (backend → StatusPill):
 *   scheduled → confirmed  (staff-booked appt is a confirmed booking)
 *   no_show   → noshow
 *   cancelled → cancelled
 *   completed → completed
 *
 * Design deviations from the source:
 * - Day window 07:00–21:00 (not 09:00–19:00) to cover early/late appts.
 * - Buffers: not in the data model — omitted.
 * - Reschedule/cancel actions stay inline in the card (small buttons) rather
 *   than a context menu, because the slide-over pattern for inline edit adds
 *   complexity without test-backing.
 */

/* ── Types ──────────────────────────────────────────────────────────────── */
type ApptRow = {
  id: string;
  bookingId: string;
  serviceName: string;
  staffId: string;
  staffName: string;
  startsAt: Date;
  endsAt: Date;
  status: string;
  clientName: string | null;
  clientPhone: string | null;
};

type StaffRow = { id: string; displayName: string; isActive: boolean };
// durationMinutes: service.list already returns it — needed to compute the
// new appointment's endsAt for the T30 overlap-confirm dialog's minute count.
type ServiceRow = { id: string; name: string; isActive: boolean; durationMinutes: number };

/**
 * T30 — the specific appointment a genuine overlap conflicts with, when the
 * business has opted into overlap-confirm. Parsed out of the API's stable
 * `slot-unavailable:overlap-confirm:<json>` message (see overlap-conflict.ts)
 * — still starts with `slot-unavailable` so anything that only checks that
 * prefix keeps working; this parses the richer payload.
 */
type OverlapConflict = {
  appointmentId: string;
  staffId: string;
  staffName: string;
  clientName: string | null;
  serviceName: string;
  startsAt: string;
  endsAt: string;
};

const OVERLAP_CONFLICT_PREFIX = 'slot-unavailable:overlap-confirm:';

function parseOverlapConflict(message: string): OverlapConflict | null {
  if (!message.startsWith(OVERLAP_CONFLICT_PREFIX)) return null;
  try {
    return JSON.parse(message.slice(OVERLAP_CONFLICT_PREFIX.length)) as OverlapConflict;
  } catch {
    return null;
  }
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function fmtTime(d: Date): string {
  return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function dayBounds(dateStr: string): { from: Date; to: Date } {
  const from = new Date(`${dateStr}T00:00:00`);
  const next = new Date(from);
  next.setDate(next.getDate() + 1);
  const nextStr = toDateStr(next);
  return { from, to: new Date(`${nextStr}T00:00:00`) };
}

function formatDisplayDate(ds: string): string {
  const d = new Date(`${ds}T12:00:00`);
  return d.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' });
}

/* ── T30: overlap-confirm dialog ────────────────────────────────────────────
   Side-by-side clash cards — the two appointments shown explicitly against
   each other, a dashed divider between them, book-anyway/pick-another
   actions. Shared by both the new-booking flow and the reschedule panel. ── */
type OverlapCardInfo = {
  clientName: string | null;
  serviceName: string;
  staffName: string;
  startsAt: Date;
  endsAt: Date;
};

function overlapMinutes(a: OverlapCardInfo, b: OverlapCardInfo): number {
  const start = Math.max(a.startsAt.getTime(), b.startsAt.getTime());
  const end = Math.min(a.endsAt.getTime(), b.endsAt.getTime());
  return Math.max(0, Math.round((end - start) / 60_000));
}

function OverlapConfirmDialog(props: {
  existing: OverlapCardInfo;
  incoming: OverlapCardInfo;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const minutes = overlapMinutes(props.existing, props.incoming);

  const card = (info: OverlapCardInfo, tone: 'existing' | 'incoming') => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        background: 'var(--bg-canvas)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--r-md)',
        padding: '0.625rem 0.75rem',
      }}
    >
      <span
        style={{
          alignSelf: 'stretch',
          width: 3,
          borderRadius: 2,
          background: tone === 'existing' ? 'var(--brand)' : 'var(--accent)',
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-primary)',
            fontSize: 'var(--fs-body)',
          }}
        >
          {info.clientName ?? info.serviceName}
        </div>
        <div
          style={{
            fontSize: 'var(--fs-caption)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-secondary)',
          }}
        >
          {info.serviceName} · {info.staffName}
        </div>
      </div>
      <div
        dir="ltr"
        style={{
          fontSize: 'var(--fs-caption)',
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-secondary)',
        }}
      >
        {fmtTime(info.startsAt)}–{fmtTime(info.endsAt)}
      </div>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('calendar.overlap_conflict_title', { minutes })}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-lg)',
          padding: '1.5rem',
          minWidth: '22rem',
          maxWidth: '90vw',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <p
          style={{
            margin: '0 0 1rem',
            fontWeight: 600,
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-primary)',
            fontSize: 'var(--fs-body-l)',
          }}
        >
          {t('calendar.overlap_conflict_title', { minutes })}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {card(props.existing, 'existing')}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: 'var(--fs-caption)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              color: 'var(--warn)',
              padding: '0 0.125rem',
            }}
          >
            <span style={{ flex: 1, borderTop: '1px dashed var(--warn)' }} aria-hidden="true" />
            {t('calendar.overlap_conflict_divider')}
            <span style={{ flex: 1, borderTop: '1px dashed var(--warn)' }} aria-hidden="true" />
          </div>
          {card(props.incoming, 'incoming')}
        </div>

        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.25rem' }}
        >
          <Button variant="primary" size="md" disabled={props.isPending} onClick={props.onConfirm}>
            {props.isPending ? t('calendar.overlap_confirming') : t('calendar.overlap_book_anyway')}
          </Button>
          <Button variant="secondary" size="md" disabled={props.isPending} onClick={props.onCancel}>
            {t('calendar.overlap_pick_another')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── T31: hours-override confirm dialog ─────────────────────────────────────
   Single-card variant of OverlapConfirmDialog — there's no second "clashing
   appointment" to show here, just a reason (business opted in to letting
   staff book outside their configured working hours). ── */
function HoursOverrideDialog(props: {
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('calendar.hours_override_title')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-lg)',
          padding: '1.5rem',
          minWidth: '22rem',
          maxWidth: '90vw',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <p
          style={{
            margin: '0 0 0.75rem',
            fontWeight: 600,
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-primary)',
            fontSize: 'var(--fs-body-l)',
          }}
        >
          {t('calendar.hours_override_title')}
        </p>
        <p
          style={{
            margin: '0 0 1.25rem',
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--fs-body)',
          }}
        >
          {t('calendar.hours_override_body')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Button variant="primary" size="md" disabled={props.isPending} onClick={props.onConfirm}>
            {props.isPending ? t('calendar.overlap_confirming') : t('calendar.overlap_book_anyway')}
          </Button>
          <Button variant="secondary" size="md" disabled={props.isPending} onClick={props.onCancel}>
            {t('calendar.overlap_pick_another')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Constants ───────────────────────────────────────────────────────────── */
const DAY_START = 7; // 07:00
const DAY_END = 21; // 21:00
const HOUR = 64; // px per hour
const DAY_HEIGHT = (DAY_END - DAY_START) * HOUR; // 896 px

// Backend status → StatusPill prop
type PillStatus = 'confirmed' | 'pending' | 'cancelled' | 'completed' | 'noshow';
const STATUS_PILL: Record<string, PillStatus> = {
  scheduled: 'confirmed',
  no_show: 'noshow',
  cancelled: 'cancelled',
  completed: 'completed',
};

// Backend status → left-border stripe color
const STATUS_STRIPE: Record<string, string> = {
  scheduled: 'var(--success)',
  no_show: 'var(--text-tertiary)',
  cancelled: 'var(--error)',
  completed: 'var(--info)',
};

/* ── Shared input style (native inputs/selects not replaced by DS — the DS
      Input is a controlled component that adds complexity to the test
      getByLabelText lookups; keep native inputs with DS tokens.) ─────────── */
const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--r-sm)',
  fontSize: '0.9375rem',
  fontFamily: 'var(--font-sans)',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
};

export type StaffScheduleData = {
  workingHours: WorkingHoursEntry[];
  breaks: BreakEntry[];
  timeOff: TimeOffEntry[];
};

/* ── One `staff.getSchedule` query per visible staff column, reported up via
      callback — same "loader" convention as the mobile day grid's
      `StaffShiftLoader` (apps/mobile/app/(tabs)/biz-calendar.tsx), needed
      because a variable number of staff can't each call a hook directly
      inside a `.map`. Renders nothing; only visible (not every active)
      staff are loaded, since that's all the grid currently draws. ────────── */
function StaffScheduleLoader(props: {
  staffId: string;
  onSchedule: (staffId: string, schedule: StaffScheduleData | null) => void;
}) {
  const { staffId, onSchedule } = props;
  const scheduleQuery = trpc.staff.getSchedule.useQuery({ staffId });
  useEffect(() => {
    if (!scheduleQuery.data) return;
    onSchedule(staffId, scheduleQuery.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleQuery.data, staffId]);
  return null;
}

/* ── Component ───────────────────────────────────────────────────────────── */
export default function CalendarPage() {
  const { t } = useTranslation();

  /* ── Date navigation ──────────────────────────────────── */
  const [selectedDate, setSelectedDate] = useState<string>(() => toDateStr(new Date()));
  const [staffFilter, setStaffFilter] = useState<string>('');

  const { from, to } = dayBounds(selectedDate);

  // The `T12:00:00` noon anchor is load-bearing, not decoration: parsed at
  // midnight, a day that a DST shift moves by an hour lands back on the
  // previous date and the arrow appears to do nothing.
  const shiftDay = (delta: number) => {
    const d = new Date(`${selectedDate}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setSelectedDate(toDateStr(d));
  };
  const goToPrev = () => shiftDay(-1);
  const goToNext = () => shiftDay(1);

  /* ── tRPC ─────────────────────────────────────────────── */
  const calendarQuery = trpc.booking.calendar.useQuery(
    { from, to, staffId: staffFilter || undefined },
    { retry: 1 },
  );
  const staffQuery = trpc.staff.list.useQuery(undefined, { retry: 1 });
  const serviceQuery = trpc.service.list.useQuery(undefined, { retry: 1 });

  // "No staff working these hours" hatch — one schedule per visible staff
  // column, keyed by staffId (see `StaffScheduleLoader` above).
  const dayOfWeek = from.getDay(); // 0=Sunday, matches working_hours/recurring_break's day_of_week
  const [staffSchedules, setStaffSchedules] = useState<Record<string, StaffScheduleData | null>>(
    {},
  );
  // Same-value guard, ported verbatim from the mobile twin's `reportTeamSchedule`
  // (`app/(tabs)/biz-calendar.tsx`). Without it every loader commits state on
  // every revalidation, so N staff means N unconditional re-renders of this
  // whole page — at exactly the moment the user is waiting for the grid. React
  // Query hands back stable array identities, so reference equality is the
  // right test here.
  const handleStaffSchedule = (staffId: string, schedule: StaffScheduleData | null) => {
    setStaffSchedules((prev) => {
      const existing = prev[staffId];
      const same =
        existing === schedule ||
        (existing != null &&
          schedule != null &&
          existing.workingHours === schedule.workingHours &&
          existing.breaks === schedule.breaks &&
          existing.timeOff === schedule.timeOff);
      return same ? prev : { ...prev, [staffId]: schedule };
    });
  };

  const utils = trpc.useUtils();
  const invalidate = () => void utils.booking.calendar.invalidate();

  const upsertClient = trpc.clients.upsertByPhone.useMutation();
  const createBooking = trpc.booking.create.useMutation({ onSuccess: invalidate });
  const reschedule = trpc.appointment.reschedule.useMutation({ onSuccess: invalidate });
  const cancelBooking = trpc.booking.cancel.useMutation({ onSuccess: invalidate });

  /* ── New-booking form ─────────────────────────────────── */
  const [showNewForm, setShowNewForm] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');
  const [newServiceId, setNewServiceId] = useState('');
  const [newStaffId, setNewStaffId] = useState('');
  const [newDateTime, setNewDateTime] = useState('');
  const [newMsg, setNewMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const isNewSubmitting = upsertClient.isPending || createBooking.isPending;
  // T30 — set only when the API reports a genuine overlap AND this business
  // has opted in; `pending` is exactly what's needed to retry the same
  // create with `confirmOverlapWithAppointmentId` once the user confirms.
  const [newOverlapConflict, setNewOverlapConflict] = useState<{
    conflict: OverlapConflict;
    pending: {
      idempotencyKey: string;
      clientId: string;
      serviceId: string;
      staffId: string;
      startsAt: Date;
    };
  } | null>(null);
  // T31 — set when the API reports the business opted into hours-override and
  // the requested slot falls outside the staff member's working hours.
  const [newHoursOverride, setNewHoursOverride] = useState(false);

  // Map a booking/reschedule error message to user-facing text. Shared by the
  // reschedule + hours-override + overlap confirm paths (identical mapping);
  // handleNewBooking and confirmNewOverlap keep their own richer fallbacks.
  const bookingErrorText = (msg: string): string => {
    if (msg.startsWith('duplicate-service-booking')) return t('calendar.duplicate_service_booking');
    if (msg.startsWith('slot-unavailable')) return t('calendar.slot_unavailable');
    return t('common.error');
  };

  const resetNewForm = () => {
    setNewPhone('');
    setNewName('');
    setNewServiceId('');
    setNewStaffId('');
    setNewDateTime('');
    setNewMsg(null);
    setNewOverlapConflict(null);
    setNewHoursOverride(false);
  };

  const handleNewBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewMsg(null);
    if (!newPhone.trim() || !newServiceId || !newStaffId || !newDateTime) return;

    try {
      const client = await upsertClient.mutateAsync({
        phone: newPhone.trim(),
        fullName: newName.trim() || undefined,
      });

      const startsAt = new Date(newDateTime);
      const idempotencyKey = crypto.randomUUID();

      await createBooking.mutateAsync({
        idempotencyKey,
        source: 'staff_manual',
        clientId: client.id,
        items: [{ serviceId: newServiceId, staffId: newStaffId, startsAt }],
      });

      setNewMsg({ type: 'success', text: t('calendar.booking_success') });
      resetNewForm();
      setShowNewForm(false);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? '';
      const conflict = parseOverlapConflict(msg);
      if (conflict) {
        setNewOverlapConflict({
          conflict,
          pending: {
            idempotencyKey: crypto.randomUUID(),
            clientId: '', // re-resolved fresh in confirmNewOverlap (phone may have changed since)
            serviceId: newServiceId,
            staffId: newStaffId,
            startsAt: new Date(newDateTime),
          },
        });
      } else if (msg === 'slot-unavailable:hours-override-available') {
        setNewHoursOverride(true);
      } else if (msg.startsWith('slot-unavailable')) {
        setNewMsg({ type: 'error', text: t('calendar.slot_unavailable') });
      } else if (msg.startsWith('duplicate-service-booking')) {
        setNewMsg({ type: 'error', text: t('calendar.duplicate_service_booking') });
      } else if (msg.toLowerCase().includes('phone') || msg.toLowerCase().includes('invalid')) {
        setNewMsg({ type: 'error', text: t('calendar.phone_invalid') });
      } else {
        setNewMsg({ type: 'error', text: msg || t('common.error') });
      }
    }
  };

  const confirmNewHoursOverride = async () => {
    setNewMsg(null);
    try {
      // Re-resolve the client (upsert is idempotent) rather than trust a
      // stashed id, in case the phone/name fields changed while the confirm
      // dialog was open — same rationale as confirmNewOverlap below.
      const client = await upsertClient.mutateAsync({
        phone: newPhone.trim(),
        fullName: newName.trim() || undefined,
      });
      await createBooking.mutateAsync({
        idempotencyKey: crypto.randomUUID(),
        source: 'staff_manual',
        clientId: client.id,
        items: [{ serviceId: newServiceId, staffId: newStaffId, startsAt: new Date(newDateTime) }],
        confirmHoursOverride: true,
      });
      setNewHoursOverride(false);
      setNewMsg({ type: 'success', text: t('calendar.booking_success') });
      resetNewForm();
      setShowNewForm(false);
    } catch (err: unknown) {
      setNewHoursOverride(false);
      const msg = (err as { message?: string })?.message ?? '';
      setNewMsg({ type: 'error', text: bookingErrorText(msg) });
    }
  };

  const confirmNewOverlap = async () => {
    if (!newOverlapConflict) return;
    setNewMsg(null);
    try {
      // Re-resolve the client (upsert is idempotent) rather than trust a
      // stashed id, in case the phone/name fields changed while the confirm
      // dialog was open.
      const client = await upsertClient.mutateAsync({
        phone: newPhone.trim(),
        fullName: newName.trim() || undefined,
      });
      await createBooking.mutateAsync({
        idempotencyKey: newOverlapConflict.pending.idempotencyKey,
        source: 'staff_manual',
        clientId: client.id,
        items: [
          {
            serviceId: newOverlapConflict.pending.serviceId,
            staffId: newOverlapConflict.pending.staffId,
            startsAt: newOverlapConflict.pending.startsAt,
          },
        ],
        confirmOverlapWithAppointmentId: newOverlapConflict.conflict.appointmentId,
      });
      setNewOverlapConflict(null);
      setNewMsg({ type: 'success', text: t('calendar.booking_success') });
      resetNewForm();
      setShowNewForm(false);
    } catch (err: unknown) {
      setNewOverlapConflict(null);
      const msg = (err as { message?: string })?.message ?? '';
      setNewMsg({
        type: 'error',
        text: msg.startsWith('duplicate-service-booking')
          ? t('calendar.duplicate_service_booking')
          : msg.startsWith('slot-unavailable')
            ? t('calendar.slot_unavailable')
            : msg,
      });
    }
  };

  /* ── Reschedule panel ─────────────────────────────────── */
  const [reschAppt, setReschAppt] = useState<ApptRow | null>(null);
  const [reschDateTime, setReschDateTime] = useState('');
  const [reschStaffId, setReschStaffId] = useState('');
  const [reschMsg, setReschMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null,
  );
  // T30 — same pattern as newOverlapConflict above, for the reschedule panel.
  const [reschOverlapConflict, setReschOverlapConflict] = useState<OverlapConflict | null>(null);
  // T31 — same pattern as newHoursOverride above, for the reschedule panel.
  const [reschHoursOverride, setReschHoursOverride] = useState(false);

  const openReschedule = (appt: ApptRow) => {
    setReschAppt(appt);
    const d = appt.startsAt;
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
    setReschDateTime(local);
    setReschStaffId(appt.staffId);
    setReschMsg(null);
    setReschOverlapConflict(null);
    setReschHoursOverride(false);
  };

  const handleReschedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reschAppt || !reschDateTime) return;
    setReschMsg(null);

    try {
      await reschedule.mutateAsync({
        appointmentId: reschAppt.id,
        startsAt: new Date(reschDateTime),
        staffId: reschStaffId !== reschAppt.staffId ? reschStaffId : undefined,
      });
      setReschMsg({ type: 'success', text: t('calendar.reschedule_success') });
      setReschAppt(null);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? '';
      const conflict = parseOverlapConflict(msg);
      if (conflict) {
        setReschOverlapConflict(conflict);
        return;
      }
      if (msg === 'slot-unavailable:hours-override-available') {
        setReschHoursOverride(true);
        return;
      }
      setReschMsg({ type: 'error', text: bookingErrorText(msg) });
    }
  };

  const confirmReschHoursOverride = async () => {
    if (!reschAppt || !reschDateTime) return;
    setReschMsg(null);
    try {
      await reschedule.mutateAsync({
        appointmentId: reschAppt.id,
        startsAt: new Date(reschDateTime),
        staffId: reschStaffId !== reschAppt.staffId ? reschStaffId : undefined,
        confirmHoursOverride: true,
      });
      setReschHoursOverride(false);
      setReschMsg({ type: 'success', text: t('calendar.reschedule_success') });
      setReschAppt(null);
    } catch (err: unknown) {
      setReschHoursOverride(false);
      const msg = (err as { message?: string })?.message ?? '';
      setReschMsg({ type: 'error', text: bookingErrorText(msg) });
    }
  };

  const confirmReschOverlap = async () => {
    if (!reschAppt || !reschDateTime || !reschOverlapConflict) return;
    setReschMsg(null);
    try {
      await reschedule.mutateAsync({
        appointmentId: reschAppt.id,
        startsAt: new Date(reschDateTime),
        staffId: reschStaffId !== reschAppt.staffId ? reschStaffId : undefined,
        confirmOverlapWithAppointmentId: reschOverlapConflict.appointmentId,
      });
      setReschOverlapConflict(null);
      setReschMsg({ type: 'success', text: t('calendar.reschedule_success') });
      setReschAppt(null);
    } catch (err: unknown) {
      setReschOverlapConflict(null);
      const msg = (err as { message?: string })?.message ?? '';
      setReschMsg({ type: 'error', text: bookingErrorText(msg) });
    }
  };

  /* ── Cancel ───────────────────────────────────────────── */
  const [cancelPendingAppt, setCancelPendingAppt] = useState<ApptRow | null>(null);
  const [cancelMsg, setCancelMsg] = useState<string | null>(null);

  const handleCancel = (appt: ApptRow) => {
    setCancelMsg(null);
    setCancelPendingAppt(appt);
  };

  /**
   * The dialog closes on SUCCESS only.
   *
   * It used to clear `cancelPendingAppt` BEFORE the await and swallow the
   * rejection with `.catch(() => undefined)`, so a failed cancel looked exactly
   * like a successful one: the dialog vanished and the owner was told nothing.
   * They would believe a customer's booking was cancelled when it was not — and
   * the calendar only contradicted them on the next refetch.
   *
   * Same error vocabulary as reschedule and new-booking (`bookingErrorText`),
   * so a slot/duplicate failure reads the same wherever it happens.
   */
  const handleCancelConfirm = async () => {
    if (!cancelPendingAppt) return;
    const appt = cancelPendingAppt;
    setCancelMsg(null);
    try {
      await cancelBooking.mutateAsync({ bookingId: appt.bookingId });
      setCancelPendingAppt(null);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? '';
      setCancelMsg(bookingErrorText(msg));
    }
  };

  const handleCancelDismiss = () => {
    setCancelMsg(null);
    setCancelPendingAppt(null);
  };

  /* ── Normalise data ───────────────────────────────────── */
  const appointments: ApptRow[] = (calendarQuery.data ?? []).map((r) => ({
    ...r,
    startsAt: r.startsAt instanceof Date ? r.startsAt : new Date(r.startsAt),
    endsAt: r.endsAt instanceof Date ? r.endsAt : new Date(r.endsAt),
  }));

  const staffList: StaffRow[] = (staffQuery.data ?? []).filter(
    (s) => (s as StaffRow).isActive,
  ) as StaffRow[];

  const serviceList: ServiceRow[] = (serviceQuery.data ?? []).filter(
    (s) => (s as ServiceRow).isActive,
  ) as ServiceRow[];

  /* ── Derived ──────────────────────────────────────────── */
  const visibleStaff = staffFilter ? staffList.filter((s) => s.id === staffFilter) : staffList;

  // Appointment positioning from real data
  const apptTopPx = (a: ApptRow): number =>
    Math.max(0, (a.startsAt.getHours() + a.startsAt.getMinutes() / 60 - DAY_START) * HOUR);

  const apptHeightPx = (a: ApptRow): number =>
    Math.max(30, ((a.endsAt.getTime() - a.startsAt.getTime()) / 3_600_000) * HOUR);

  // Now-line
  const isToday = selectedDate === toDateStr(new Date());
  const now = new Date();
  const nowTop = (now.getHours() + now.getMinutes() / 60 - DAY_START) * HOUR;
  const showNowLine = isToday && nowTop >= 0 && nowTop <= DAY_HEIGHT;

  const isSaturday = new Date(`${selectedDate}T12:00:00`).getDay() === 6;

  /* ─────────────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ── Control bar ─────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '10px 20px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--bg-surface)',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        {/* Inline-start (right in RTL): date navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={goToPrev} aria-label="יום קודם">
            <Icon name="chevron-right" size={16} aria-hidden />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setSelectedDate(toDateStr(new Date()))}
          >
            {t('calendar.today')}
          </Button>
          <Button variant="secondary" size="sm" onClick={goToNext} aria-label="יום הבא">
            <Icon name="chevron-left" size={16} aria-hidden />
          </Button>
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-body-l)',
              paddingInlineStart: 8,
              color: 'var(--text-primary)',
            }}
          >
            {formatDisplayDate(selectedDate)}
          </span>
        </div>

        {/* Inline-end (left in RTL): staff filter + new booking */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Staff avatar filter */}
          <div style={{ display: 'flex', gap: 4 }} aria-label={t('calendar.filter_staff')}>
            {staffList.map((s, i) => {
              const isSelected = staffFilter === s.id;
              const isAny = staffFilter === '';
              return (
                <button
                  key={s.id}
                  onClick={() => setStaffFilter(isSelected ? '' : s.id)}
                  aria-label={s.displayName}
                  aria-pressed={isSelected}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    opacity: isAny || isSelected ? 1 : 0.4,
                    transition: 'opacity var(--dur-micro) var(--ease-standard)',
                  }}
                >
                  <Avatar name={s.displayName} size={32} ring={i + 1} />
                </button>
              );
            })}
          </div>

          {/* New booking button */}
          <Button
            variant="primary"
            size="sm"
            data-testid="new-appt"
            leadingIcon={<Icon name="plus" size={16} aria-hidden />}
            onClick={() => {
              setShowNewForm(true);
              setNewMsg(null);
            }}
          >
            {t('calendar.new_booking')}
          </Button>
        </div>
      </div>

      {/* ── Status messages ──────────────────────────────── */}
      {calendarQuery.isPending && (
        <p style={{ padding: 24, color: 'var(--text-secondary)' }}>{t('calendar.loading')}</p>
      )}

      {calendarQuery.error && (
        <p role="alert" style={{ padding: '12px 24px', color: 'var(--error)' }}>
          {t('common.error')}: {calendarQuery.error.message}
        </p>
      )}

      {calendarQuery.data && appointments.length === 0 && (
        <p style={{ padding: '12px 24px', color: 'var(--text-secondary)', textAlign: 'center' }}>
          {t('calendar.no_appointments')}
        </p>
      )}

      {/* Shabbat note */}
      {isSaturday && (
        <p
          style={{
            padding: '6px 20px',
            textAlign: 'center',
            flexShrink: 0,
            color: 'var(--text-secondary)',
            fontSize: 'var(--fs-caption)',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--bg-sunken)',
          }}
        >
          שבת - סגור
        </p>
      )}

      {/* ── Day grid ─────────────────────────────────────── */}
      {!calendarQuery.isPending && (
        <div
          data-testid="calendar-day"
          style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', position: 'relative' }}
        >
          {visibleStaff.map((s) => (
            <StaffScheduleLoader key={s.id} staffId={s.id} onSchedule={handleStaffSchedule} />
          ))}

          {/* Sticky column headers */}
          <div
            style={{
              display: 'flex',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              background: 'var(--bg-surface)',
              borderBottom: '1px solid var(--border-subtle)',
              minWidth: `calc(56px + ${Math.max(visibleStaff.length, 1)} * 120px)`,
            }}
          >
            {/* Time axis header spacer */}
            <div style={{ width: 56, flexShrink: 0 }} />

            {/* Staff column headers (row-reverse = right→left in RTL) */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'row-reverse' }}>
              {visibleStaff.map((s, i) => (
                <div
                  key={s.id}
                  data-testid="staff-col"
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderInlineStart: '1px solid var(--border-subtle)',
                    minWidth: 120,
                  }}
                >
                  <Avatar name={s.displayName} size={28} ring={i + 1} />
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--fs-body)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {s.displayName}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Time axis + appointment columns */}
          <div
            style={{
              display: 'flex',
              height: DAY_HEIGHT,
              position: 'relative',
              minWidth: `calc(56px + ${Math.max(visibleStaff.length, 1)} * 120px)`,
            }}
          >
            {/* Time axis (inline-end = right in RTL) */}
            <div style={{ width: 56, flexShrink: 0, position: 'relative' }}>
              {Array.from({ length: DAY_END - DAY_START }, (_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    top: i * HOUR,
                    right: 0,
                    width: '100%',
                    paddingInlineEnd: 8,
                    textAlign: 'end',
                    fontSize: 'var(--fs-micro)',
                    color: 'var(--text-tertiary)',
                    transform: 'translateY(-50%)',
                    userSelect: 'none',
                  }}
                >
                  <Clock value={`${String(DAY_START + i).padStart(2, '0')}:00`} />
                </div>
              ))}
            </div>

            {/* Staff columns (row-reverse for RTL: first staff on the right) */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row-reverse',
                position: 'relative',
              }}
            >
              {/* Horizontal hour-separator lines */}
              {Array.from({ length: DAY_END - DAY_START }, (_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    top: i * HOUR,
                    left: 0,
                    right: 0,
                    borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />
              ))}

              {/* Brand now-line */}
              {showNowLine && (
                <div
                  style={{
                    position: 'absolute',
                    top: nowTop,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: 'var(--brand)',
                    zIndex: 5,
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      insetInlineEnd: -5,
                      top: -4,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: 'var(--brand)',
                    }}
                  />
                </div>
              )}

              {/* One column per visible staff member */}
              {visibleStaff.map((s) => {
                const staffAppts = appointments.filter((a) => a.staffId === s.id);
                const schedule = staffSchedules[s.id];
                // Bands stay empty (no hatch) while the schedule is still
                // loading — flashing a full-column hatch for a beat would
                // misread as "nobody's working" before we actually know.
                const blockedBands = schedule
                  ? blockedBandsForStaffDay({
                      dayOfWeek,
                      workingHours: schedule.workingHours,
                      breaks: schedule.breaks,
                      timeOff: schedule.timeOff,
                      dayBounds: { from, to },
                      gridStartMin: DAY_START * 60,
                      gridEndMin: DAY_END * 60,
                    })
                  : [];
                const offToday = isFullyBlocked(blockedBands, DAY_START * 60, DAY_END * 60);
                const todayHours = schedule?.workingHours.find((wh) => wh.dayOfWeek === dayOfWeek);
                const minToPx = (min: number) => ((min - DAY_START * 60) / 60) * HOUR;

                return (
                  <div
                    key={s.id}
                    style={{
                      flex: 1,
                      position: 'relative',
                      borderInlineStart: '1px solid var(--border-subtle)',
                      minWidth: 120,
                    }}
                  >
                    {/* "No staff working these hours" hatch — a quiet 45°
                        ink texture (not a warning color), same treatment as
                        the mobile day grid's blocked-band overlay. */}
                    {blockedBands.map((band, i) => (
                      <div
                        key={i}
                        data-testid="blocked-band-hatch"
                        aria-hidden
                        style={{
                          position: 'absolute',
                          insetInlineStart: 0,
                          insetInlineEnd: 0,
                          top: minToPx(band.startMin),
                          height: minToPx(band.endMin) - minToPx(band.startMin),
                          pointerEvents: 'none',
                          opacity: 0.07,
                          backgroundImage:
                            'repeating-linear-gradient(45deg, var(--ink) 0, var(--ink) 1px, transparent 1px, transparent 8px)',
                        }}
                      />
                    ))}

                    {/* Shift start/end — a dashed whisper-indigo line, so the
                        eye can see exactly where the hatch begins/ends. */}
                    {todayHours &&
                      [todayHours.startTime, todayHours.endTime].map((edge, i) => {
                        const edgeMin = hhmmToMin(edge);
                        if (edgeMin <= DAY_START * 60 || edgeMin >= DAY_END * 60) return null;
                        return (
                          <div
                            key={i}
                            aria-hidden
                            style={{
                              position: 'absolute',
                              insetInlineStart: 0,
                              insetInlineEnd: 0,
                              top: minToPx(edgeMin),
                              borderTop: '1px dashed var(--accent)',
                              pointerEvents: 'none',
                            }}
                          />
                        );
                      })}

                    {/* A whole day off has no shift line and no appointments
                        to anchor on — label it, or an all-hatch column with
                        nothing else reads like a loading/error state. */}
                    {offToday && (
                      <div
                        aria-hidden
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        <span
                          style={{
                            background: 'var(--bg-surface)',
                            border: '1px solid var(--border-subtle)',
                            borderRadius: 999,
                            padding: '4px 12px',
                            fontSize: 'var(--fs-micro)',
                            fontWeight: 600,
                            color: 'var(--text-tertiary)',
                            boxShadow: 'var(--shadow-sm)',
                          }}
                        >
                          {t('calendar.staff_off_today')}
                        </span>
                      </div>
                    )}

                    {staffAppts.map((appt) => {
                      const top = apptTopPx(appt);
                      const height = apptHeightPx(appt);
                      const stripe = STATUS_STRIPE[appt.status] ?? 'var(--warn)';
                      const isMuted = appt.status === 'cancelled' || appt.status === 'no_show';

                      return (
                        <div
                          key={appt.id}
                          data-testid="appt-card"
                          style={{
                            position: 'absolute',
                            top,
                            insetInlineStart: 4,
                            insetInlineEnd: 4,
                            height: Math.max(height - 2, 28),
                            background: 'var(--bg-surface)',
                            border: '1px solid var(--border-subtle)',
                            borderRadius: 'var(--r-sm)',
                            borderInlineStart: `3px solid ${stripe}`,
                            padding: '3px 6px',
                            overflow: 'hidden',
                            opacity: isMuted ? 0.55 : 1,
                            boxShadow: 'var(--shadow-sm)',
                            zIndex: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {/* Client name (primary label) */}
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 'var(--fs-caption)',
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'var(--text-primary)',
                            }}
                          >
                            {appt.clientName ?? appt.clientPhone ?? appt.serviceName}
                          </div>

                          {/* Service name — always in DOM (may be visually clipped at small heights) */}
                          <div
                            style={{
                              fontSize: 'var(--fs-micro)',
                              color: 'var(--text-secondary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {appt.serviceName}
                          </div>

                          {/* Time range — shown only when card is tall enough */}
                          {height > 52 && (
                            <div
                              style={{
                                fontSize: 'var(--fs-micro)',
                                color: 'var(--text-tertiary)',
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                              }}
                            >
                              <Clock value={fmtTime(appt.startsAt)} />
                              <span aria-hidden>-</span>
                              <Clock value={fmtTime(appt.endsAt)} />
                            </div>
                          )}

                          {/* Status pill */}
                          {height > 44 && (
                            <StatusPill
                              status={STATUS_PILL[appt.status] ?? 'pending'}
                              style={{ marginBlockStart: 'auto', alignSelf: 'flex-start' }}
                            />
                          )}

                          {/* Reschedule / cancel actions — scheduled only */}
                          {appt.status === 'scheduled' && (
                            <div
                              style={{
                                display: 'flex',
                                gap: 3,
                                flexWrap: 'wrap',
                                marginBlockStart: 2,
                              }}
                            >
                              <button
                                aria-label={`${t('calendar.reschedule')} ${appt.serviceName}`}
                                onClick={() => openReschedule(appt)}
                                style={{
                                  fontSize: 10,
                                  border: '1px solid var(--border-strong)',
                                  borderRadius: 'var(--r-sm)',
                                  background: 'var(--bg-surface)',
                                  cursor: 'pointer',
                                  padding: '1px 5px',
                                  color: 'var(--text-primary)',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--font-sans)',
                                }}
                              >
                                {t('calendar.reschedule')}
                              </button>
                              <button
                                aria-label={`${t('calendar.cancel_appt')} ${appt.serviceName}`}
                                onClick={() => handleCancel(appt)}
                                style={{
                                  fontSize: 10,
                                  border: '1px solid var(--error)',
                                  borderRadius: 'var(--r-sm)',
                                  background: 'transparent',
                                  cursor: 'pointer',
                                  padding: '1px 5px',
                                  color: 'var(--error)',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--font-sans)',
                                }}
                              >
                                {t('calendar.cancel_appt')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          New-booking slide-over
          Wired to the same booking.create + upsertClient
          flow as the original form. All field IDs/labels
          preserved so test getByLabelText selectors pass.
          ══════════════════════════════════════════════════ */}
      {showNewForm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50 }}>
          {/* Backdrop */}
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }}
            onClick={() => {
              setShowNewForm(false);
              setNewMsg(null);
            }}
          />

          {/* Panel slides from inline-end (right in LTR, left in RTL) */}
          <div
            style={{
              position: 'absolute',
              insetBlockStart: 0,
              insetBlockEnd: 0,
              insetInlineEnd: 0,
              width: 420,
              maxWidth: '100vw',
              background: 'var(--bg-surface)',
              boxShadow: '-4px 0 32px rgba(0,0,0,0.18)',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1,
            }}
          >
            <form
              onSubmit={(e) => void handleNewBooking(e)}
              aria-label={t('calendar.booking_form_title')}
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <h2
                style={{
                  fontSize: 'var(--fs-title2)',
                  fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'],
                  marginBlock: 0,
                  color: 'var(--text-primary)',
                }}
              >
                {t('calendar.booking_form_title')}
              </h2>

              {/* Phone */}
              <div>
                <label
                  htmlFor="bk-phone"
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBlockEnd: 4,
                    fontSize: 'var(--fs-body)',
                  }}
                >
                  {t('calendar.phone_label')}
                </label>
                <input
                  id="bk-phone"
                  type="tel"
                  required
                  placeholder={t('calendar.phone_placeholder')}
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  disabled={isNewSubmitting}
                  style={inputStyle}
                />
              </div>

              {/* Name */}
              <div>
                <label
                  htmlFor="bk-name"
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBlockEnd: 4,
                    fontSize: 'var(--fs-body)',
                  }}
                >
                  {t('calendar.name_label')}
                </label>
                <input
                  id="bk-name"
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  disabled={isNewSubmitting}
                  style={inputStyle}
                />
              </div>

              {/* Service */}
              <div>
                <label
                  htmlFor="bk-service"
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBlockEnd: 4,
                    fontSize: 'var(--fs-body)',
                  }}
                >
                  {t('calendar.service_label')}
                </label>
                <select
                  id="bk-service"
                  required
                  value={newServiceId}
                  onChange={(e) => setNewServiceId(e.target.value)}
                  disabled={isNewSubmitting}
                  style={inputStyle}
                >
                  <option value="">{t('calendar.select_service')}</option>
                  {serviceList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Staff */}
              <div>
                <label
                  htmlFor="bk-staff"
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBlockEnd: 4,
                    fontSize: 'var(--fs-body)',
                  }}
                >
                  {t('calendar.staff_label')}
                </label>
                <select
                  id="bk-staff"
                  required
                  value={newStaffId}
                  onChange={(e) => setNewStaffId(e.target.value)}
                  disabled={isNewSubmitting}
                  style={inputStyle}
                >
                  <option value="">{t('calendar.select_staff')}</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.displayName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date/time */}
              <div>
                <label
                  htmlFor="bk-datetime"
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBlockEnd: 4,
                    fontSize: 'var(--fs-body)',
                  }}
                >
                  {t('calendar.datetime_label')}
                </label>
                <input
                  id="bk-datetime"
                  type="datetime-local"
                  required
                  value={newDateTime}
                  onChange={(e) => setNewDateTime(e.target.value)}
                  disabled={isNewSubmitting}
                  style={inputStyle}
                />
              </div>

              {/* Error / success message */}
              {newMsg && (
                <p
                  role={newMsg.type === 'error' ? 'alert' : 'status'}
                  style={{
                    color: newMsg.type === 'error' ? 'var(--error)' : 'var(--success)',
                    fontSize: 'var(--fs-body)',
                    margin: 0,
                  }}
                >
                  {newMsg.text}
                </p>
              )}

              {/* Footer */}
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  marginBlockStart: 'auto',
                  paddingBlockStart: 8,
                  borderTop: '1px solid var(--border-subtle)',
                }}
              >
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={isNewSubmitting}
                  style={{ flex: 1 }}
                >
                  {isNewSubmitting ? t('calendar.submitting') : t('calendar.submit_booking')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    setShowNewForm(false);
                    setNewMsg(null);
                  }}
                  disabled={isNewSubmitting}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          Reschedule dialog
          ══════════════════════════════════════════════════ */}
      {reschAppt && (
        <div
          data-testid="resch-panel"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
        >
          <form
            onSubmit={(e) => void handleReschedule(e)}
            aria-label={t('calendar.reschedule_title')}
            style={{
              background: 'var(--bg-surface)',
              borderRadius: 'var(--r-lg)',
              padding: 28,
              width: 400,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <h2
              style={{
                fontSize: 'var(--fs-title2)',
                fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'],
                margin: 0,
                color: 'var(--text-primary)',
              }}
            >
              {t('calendar.reschedule_title')}
            </h2>

            <p style={{ margin: 0, fontSize: 'var(--fs-body)', color: 'var(--text-secondary)' }}>
              {reschAppt.serviceName} - {reschAppt.staffName}
            </p>

            {/* Date/time */}
            <div>
              <label
                htmlFor="rsch-datetime"
                style={{
                  display: 'block',
                  fontWeight: 500,
                  marginBlockEnd: 4,
                  fontSize: 'var(--fs-body)',
                }}
              >
                {t('calendar.datetime_label')}
              </label>
              <input
                id="rsch-datetime"
                type="datetime-local"
                required
                value={reschDateTime}
                onChange={(e) => setReschDateTime(e.target.value)}
                disabled={reschedule.isPending}
                style={inputStyle}
              />
            </div>

            {/* Staff */}
            <div>
              <label
                htmlFor="rsch-staff"
                style={{
                  display: 'block',
                  fontWeight: 500,
                  marginBlockEnd: 4,
                  fontSize: 'var(--fs-body)',
                }}
              >
                {t('calendar.staff_label')}
              </label>
              <select
                id="rsch-staff"
                value={reschStaffId}
                onChange={(e) => setReschStaffId(e.target.value)}
                disabled={reschedule.isPending}
                style={inputStyle}
              >
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName}
                  </option>
                ))}
              </select>
            </div>

            {/* Error / success */}
            {reschMsg && (
              <p
                role={reschMsg.type === 'error' ? 'alert' : 'status'}
                style={{
                  margin: 0,
                  color: reschMsg.type === 'error' ? 'var(--error)' : 'var(--success)',
                  fontSize: 'var(--fs-body)',
                }}
              >
                {reschMsg.text}
              </p>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={reschedule.isPending}
                style={{ flex: 1 }}
              >
                {reschedule.isPending ? t('calendar.submitting') : t('calendar.reschedule_submit')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={() => setReschAppt(null)}
                disabled={reschedule.isPending}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          T30 — overlap-confirm dialogs (new booking / reschedule)
          ══════════════════════════════════════════════════ */}
      {newOverlapConflict &&
        (() => {
          const svc = serviceList.find((s) => s.id === newOverlapConflict.pending.serviceId);
          const staff = staffList.find((s) => s.id === newOverlapConflict.pending.staffId);
          const durationMs = (svc?.durationMinutes ?? 30) * 60_000;
          return (
            <OverlapConfirmDialog
              existing={{
                clientName: newOverlapConflict.conflict.clientName,
                serviceName: newOverlapConflict.conflict.serviceName,
                staffName: newOverlapConflict.conflict.staffName,
                startsAt: new Date(newOverlapConflict.conflict.startsAt),
                endsAt: new Date(newOverlapConflict.conflict.endsAt),
              }}
              incoming={{
                clientName: newName.trim() || newPhone.trim() || null,
                serviceName: svc?.name ?? '',
                staffName: staff?.displayName ?? '',
                startsAt: newOverlapConflict.pending.startsAt,
                endsAt: new Date(newOverlapConflict.pending.startsAt.getTime() + durationMs),
              }}
              isPending={isNewSubmitting}
              onConfirm={() => void confirmNewOverlap()}
              onCancel={() => setNewOverlapConflict(null)}
            />
          );
        })()}

      {reschOverlapConflict &&
        reschAppt &&
        (() => {
          const staff = staffList.find((s) => s.id === reschStaffId);
          const durationMs = reschAppt.endsAt.getTime() - reschAppt.startsAt.getTime();
          const newStartsAt = new Date(reschDateTime);
          return (
            <OverlapConfirmDialog
              existing={{
                clientName: reschOverlapConflict.clientName,
                serviceName: reschOverlapConflict.serviceName,
                staffName: reschOverlapConflict.staffName,
                startsAt: new Date(reschOverlapConflict.startsAt),
                endsAt: new Date(reschOverlapConflict.endsAt),
              }}
              incoming={{
                clientName: reschAppt.clientName,
                serviceName: reschAppt.serviceName,
                staffName: staff?.displayName ?? reschAppt.staffName,
                startsAt: newStartsAt,
                endsAt: new Date(newStartsAt.getTime() + durationMs),
              }}
              isPending={reschedule.isPending}
              onConfirm={() => void confirmReschOverlap()}
              onCancel={() => setReschOverlapConflict(null)}
            />
          );
        })()}

      {/* ══════════════════════════════════════════════════
          T31 — hours-override confirm dialogs (new booking / reschedule)
          ══════════════════════════════════════════════════ */}
      {newHoursOverride && (
        <HoursOverrideDialog
          isPending={isNewSubmitting}
          onConfirm={() => void confirmNewHoursOverride()}
          onCancel={() => setNewHoursOverride(false)}
        />
      )}

      {reschHoursOverride && (
        <HoursOverrideDialog
          isPending={reschedule.isPending}
          onConfirm={() => void confirmReschHoursOverride()}
          onCancel={() => setReschHoursOverride(false)}
        />
      )}

      {/* ══════════════════════════════════════════════════
          Cancel confirmation dialog
          ══════════════════════════════════════════════════ */}
      {cancelPendingAppt && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('calendar.cancel_confirm_title')}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
        >
          <div
            style={{
              background: 'var(--bg-surface)',
              borderRadius: 'var(--r-lg)',
              padding: '1.5rem',
              minWidth: '18rem',
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
              textAlign: 'center',
            }}
          >
            <p style={{ marginBlock: '0 1.25rem', fontWeight: 500, color: 'var(--text-primary)' }}>
              {t('calendar.cancel_confirm')}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <Button
                variant="danger"
                size="md"
                disabled={cancelBooking.isPending}
                onClick={() => void handleCancelConfirm()}
              >
                {t('calendar.cancel_confirm_yes')}
              </Button>
              <Button
                variant="secondary"
                size="md"
                disabled={cancelBooking.isPending}
                onClick={handleCancelDismiss}
              >
                {t('calendar.cancel_confirm_no')}
              </Button>
            </div>
            {cancelMsg && (
              <p
                role="alert"
                style={{
                  marginBlock: '1rem 0',
                  color: 'var(--error)',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                {cancelMsg}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
